<template>
  <Select
    :options="availableReports"
    optionLabel="name"
    v-model="selectedReport"
    placeholder="Launch a Report"
    :class="props.classes"
    scroll-height="450px"
    @change="selectReport"
  ></Select>
  <TSDialog v-model:visible="launching" :label="launchTitle()" size="w-[900px]!" icon="chart-line">
    <template #content
      ><ReportLauncher
        ref="reportLauncher"
        v-if="selectedReport && launching"
        v-model:report="selectedReport"
        :project-id
        :draw-id
        :checking-account-id
        :subdivision-id
        :all-tags
        :templates
        @customized-templates="templatesCustomized"
      ></ReportLauncher
    ></template>
  </TSDialog>
</template>

<script setup lang="ts">
import { CodeTableNumber } from "@/enums/CodeTable";
import { getCodeTable } from "@/services/Utility";
import type { Code } from "@/types/Codes";
import { type ReportData, type ListOfReportData, type ReportTemplate, type ReportTemplateList } from "@/types/Reports";

const selectedReport = ref<ReportData>();
const availableReports = ref<ReportData[]>();
const allTags = ref<Code[]>([]);
const templates = ref<ReportTemplate[]>([]);
const launching = ref<boolean>(false);
const reportLauncher = ref();

interface Props {
  context: string;
  projectId?: string;
  drawId?: string;
  checkingAccountId?: string;
  subdivisionId?: string;
  classes?: string;
}
const props = withDefaults(defineProps<Props>(), {
  classes: "w-72 h-10",
});

onMounted(async (): Promise<void> => {
  populateList(props.context);
  getCodeTable(CodeTableNumber.REPORT_TAGS).then((result: Code[]) => (allTags.value = result));
});

const populateList = (context: string): void => {
  selectedReport.value = undefined;
  availableReports.value = undefined;

  axios
    .get("/reports/list/" + context)
    .then((response) => {
      handleApiResponse(
        response,
        (result: ListOfReportData) => {
          availableReports.value = result.reports;
        },
        (errors: ApiError[]) => {
          error("Could not get report list for context '" + context + "'", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const selectReport = async (): Promise<void> => {
  templates.value = [];
  if (selectedReport.value === undefined) return;

  await getTemplates();

  launching.value = true;
};

const templatesCustomized = async (): Promise<void> => {
  await getTemplates();
  if (selectedReport.value != null) {
    let defaultStillExists: boolean = false;
    templates.value.forEach((template: ReportTemplate) => {
      if (template.name === selectedReport.value?.defaultTemplate) defaultStillExists = true;
    });

    if (!defaultStillExists) selectedReport.value.defaultTemplate = "<Default Template>";
  }
  reportLauncher.value.selectDefault();
};

const getTemplates = async (): Promise<void> => {
  if (selectedReport.value === undefined) return;
  await axios
    .get("/reports/templates/" + selectedReport.value.id)
    .then((response) => {
      handleApiResponse(
        response,
        (templateList: ReportTemplateList) => {
          templates.value = templateList.templates;
        },
        (errors: ApiError[]) => {
          if (errors[0].code !== 3101) error("Error occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const isModalVisible = (): boolean => {
  return selectedReport.value !== undefined;
};

const launchTitle = (): string => {
  return selectedReport.value !== undefined ? "Launch " + selectedReport.value?.name : "Launch Report";
};
</script>
